import { IMpiPortfolioResponse } from '@kurtosys/types/portal/externalApi/providers/MPI/IMpiPortfolioResponse.js';
import { IExecutionOptions } from '@kurtosys/types/query/index.js';
import { IQueryOptionsExternalApiMpiPortfolio } from '@kurtosys/types/query/options/index.js';

import { BaseQuery } from '../../../queries/base/BaseQuery.js';

export class ExternalApiQueryMpiPortfolio extends BaseQuery<IQueryOptionsExternalApiMpiPortfolio> {
	getOptions(): IQueryOptionsExternalApiMpiPortfolio {
		const { options = {} } = this.queryOptions;
		return (
			options.externalApi &&
			options.externalApi.options &&
			options.externalApi.options.mpi &&
			options.externalApi.options.mpi.portfolio
		);
	}

	get portfolioResponse(): IMpiPortfolioResponse {
		const externalApiOptions = this.queryOptions.options && this.queryOptions.options.externalApi;
		const externalApiResponse = this.getExternalApiResponse(externalApiOptions, this.executionOptions);
		return externalApiResponse && externalApiResponse.MPI && externalApiResponse.MPI.portfolio;
	}

	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		this.executionOptions = executionOptions;
		const portfolioResponse = this.portfolioResponse;

		const options = this.options;
		if (options && portfolioResponse) {
			const { type } = options;
			if (type === 'chartData') {
				return this.getChartData<T>(portfolioResponse, options);
			}
			if (type === 'period') {
				return this.getPeriod<T>(portfolioResponse, options);
			}
		}
		return undefined;
	}

	public getPeriod<T = any>(
		portfolioResponse: IMpiPortfolioResponse,
		options: IQueryOptionsExternalApiMpiPortfolio,
	): T {
		const periodOptions = (options.options && options.options.period) || {};
		if (
			portfolioResponse &&
			portfolioResponse.portfolios &&
			portfolioResponse.portfolios.length > 0 &&
			periodOptions
		) {
			const { code } = options;
			for (const portfolio of portfolioResponse.portfolios) {
				if (portfolio.statistics) {
					const statisticForCode = portfolio.statistics.find((s) => s.code === code);
					if (statisticForCode && statisticForCode.period) {
						return statisticForCode.period as T;
					}
				}
			}
		}
		return undefined;
	}

	public getChartData<T = any>(
		portfolioResponse: IMpiPortfolioResponse,
		options: IQueryOptionsExternalApiMpiPortfolio,
	): T {
		const chartDataOptions = (options.options && options.options.chartData) || {};
		if (
			portfolioResponse &&
			portfolioResponse.portfolios &&
			portfolioResponse.portfolios.length > 0 &&
			chartDataOptions
		) {
			const { code } = options;
			if (!code) {
				return undefined;
			}
			const { groupBy = 'label' } = chartDataOptions;
			if (groupBy === 'label') {
				const rowByLabel = {};
				for (let portfolioIndex = 0; portfolioIndex < portfolioResponse.portfolios.length; portfolioIndex++) {
					const portfolio = portfolioResponse.portfolios[portfolioIndex];
					const portfolioKey = `portfolio_${portfolio.portfolioId}`;
					if (portfolio.statistics) {
						const statisticForCode = portfolio.statistics.find((s) => s.code === code);
						if (statisticForCode && statisticForCode.values && statisticForCode.values.length > 0) {
							for (const statisticValue of statisticForCode.values) {
								const { label, value } = statisticValue;
								if (!rowByLabel[label]) {
									rowByLabel[label] = { name: label };
								}
								rowByLabel[label][portfolioKey] = value;
								if (portfolioIndex === 0 && statisticForCode.period) {
									rowByLabel[label].periodStart = statisticForCode.period.start;
									rowByLabel[label].periodEnd = statisticForCode.period.end;
								}
							}
						}
					}
				}
				return Object.values(rowByLabel) as any as T;
			}
			if (groupBy === 'portfolio') {
				const rowByPortfolioKey = {};
				for (let portfolioIndex = 0; portfolioIndex < portfolioResponse.portfolios.length; portfolioIndex++) {
					const portfolio = portfolioResponse.portfolios[portfolioIndex];
					const portfolioKey = `portfolio_${portfolio.portfolioId}`;
					if (portfolio.statistics) {
						const statisticForCode = portfolio.statistics.find((s) => s.code === code);
						if (statisticForCode && statisticForCode.values && statisticForCode.values.length > 0) {
							for (const statisticValue of statisticForCode.values) {
								const { label, value } = statisticValue;
								if (!rowByPortfolioKey[portfolioKey]) {
									rowByPortfolioKey[portfolioKey] = {
										name: portfolioKey,
									};
								}
								rowByPortfolioKey[portfolioKey][label] = value;
							}
							if (statisticForCode.period) {
								rowByPortfolioKey[portfolioKey].periodStart = statisticForCode.period.start;
								rowByPortfolioKey[portfolioKey].periodEnd = statisticForCode.period.end;
							}
						}
					}
				}
				return Object.values(rowByPortfolioKey) as any as T;
			}
		}
		return undefined;
	}

	public executionOptions: IExecutionOptions | undefined;
}
